import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ApiProxy,
  IApiEpBillingAccount,
  IApiEpBillingActivefeature,
  IApiEpBillingBill,
  IApiEpBillingPlansubscription,
  IApiEpOrganizationMembership,
  IApiEpPost,
  IApiEpUserAuthorized,
  IApiProxy,
  IApiSmpAccountCredential,
  IApiSmpArtGalleryPolotnoUserTemplate,
  IApiSmpArtGalleryUpload,
  IApiSmpGptPrompt,
  IApiSmpStream,
  IApiSmpStreamDestination,
  IApiSmpStreamSource,
} from "@smp/api";
import {
  IOrganizationSetQuery,
  ISocialPageGroupSetQuery,
  IWatermark,
  IWatermarkSetQuery,
  useInfiniteSocialPageGroupSetQuery,
  usePostProjectQuery,
  useWatermarkSetQuery,
} from "@smp/graphql";
import { UiTypography } from "@smp/ui";
import { queryGetMainKey } from "@smp/utils";
import { produce } from "immer";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { UseInfiniteQueryResult, useQueryClient } from "react-query";

import { useContextSmp } from "@/context/smp";
import { useEpBillingAccount } from "@/hooks/use_ep_billing_account";
import { useEpBillingCurrentPlansubscription } from "@/hooks/use_ep_billing_current_plansubscription";
import { useEpBillingPlansubscriptionList } from "@/hooks/use_ep_billing_plansubscription";
import { useEpBillingRecurringPayment } from "@/hooks/use_ep_billing_recurring_payment";
import { useEpNotification } from "@/hooks/use_ep_notification";
import { useEpOrganizationOrganizationList } from "@/hooks/use_ep_organization";
import { useEpPageconfigListRecursive } from "@/hooks/use_ep_pageconfig";
import { useEpUsersMe } from "@/hooks/use_ep_users_me";
import { useProxyAccountContainer } from "@/hooks/use_proxy_account_container";
import { useSmpArtGalleryUploadList } from "@/hooks/use_smp_art_gallery";
import { useSmpStreamDestinationList } from "@/hooks/use_smp_stream_destination";
import { useSmpStreamStreamList } from "@/hooks/use_smp_stream_stream";
import {
  useFetchSocialPageGroup,
  useQueryGetPageConfigSetAllInvalidate,
  useQueryGptPromptList,
  useQueryOrganizationMembershipList,
  useQueryOrganizationSet,
  useQueryPageConfigSetAllUpdater,
  useQueryPolotnoBrandBooks,
  useQueryPolotnoFolders,
  useQueryPolotnoUserTemplates,
  useQuerySocialPageGroupSetInvalidate,
  useQuerySocialPageGroupSetUpdater,
} from "@/queries";
import { useQueryWatermarkConfig } from "@/queries/watermark";
import { useContextApp } from "@/screens/app/context/app";
import { dataLayerPushEcommerce } from "@/utils";

import {
  ITask,
  IWsMessage,
  iWsOrganizationMembershipUpdatedData,
  IWsOrganizationPropagatedData,
  iWsPageConfigData,
} from "./ws.types";

export const useWsTaskHandlers = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLingui();

  const { locale } = i18n;

  const {
    state: { organizationId, me },
    actions: { organizationIdChange },
  } = useContextApp();

  const socialPageGroupSetUpdate = useQuerySocialPageGroupSetUpdater();
  const socialPageGroupSetInvalidate = useQuerySocialPageGroupSetInvalidate();
  const pageConfigSetInvalidate = useQueryGetPageConfigSetAllInvalidate();
  const updatePageConfigSetAll = useQueryPageConfigSetAllUpdater();
  const { organizationList } = useEpOrganizationOrganizationList();
  const fetchSocialPageGroup = useFetchSocialPageGroup();

  const {
    actions: {
      setSmpStreamDestinationsMap,
      setSmpStreamSourcesMap,
      setSmpStreamsMap,
    },
  } = useContextSmp();

  const { accountList } = useEpBillingAccount();

  const { notificationIvalidate } = useEpNotification();

  const { meInvalidate } = useEpUsersMe();

  const accountFreePosts = accountList
    ? accountList.find((account) => account.name === "free_posts")
    : undefined;

  const taskHandlers: Partial<Record<ITask["handler"], (body: any) => void>> =
    useMemo(
      () => ({
        accountCredentialUpdated: ({
          data: accountCredential,
        }: {
          data: IApiSmpAccountCredential;
        }) => {
          queryClient.invalidateQueries(
            [
              useProxyAccountContainer.KEY,
              { path: { id: accountCredential.id } },
            ],
            { refetchInactive: true },
          );

          const queryDataList = queryClient.getQueriesData(
            useProxyAccountContainer.KEY,
          );

          const queries = queryDataList.filter((queryData) => {
            const [, data] = queryData;

            if (!data) return false;

            const accountContainerList: IApiProxy.obj["account_container"][] =
              !Array.isArray(data) ? [data] : data;

            const match = accountContainerList.some((accountContainer) =>
              accountContainer.pageContainerList.map(
                (pageContainer) =>
                  pageContainer.page.id === accountCredential.account_page_id,
              ),
            );

            return match;
          });

          if (queries) {
            queries.forEach((query) => {
              const [key] = query;
              queryClient.invalidateQueries(key);
            });
          }
        },
        appBillingAccountUpdated: (body: { data: IApiEpBillingAccount }) => {
          const account = body.data;

          if (account.name === "free_posts") {
            const before = accountFreePosts ? accountFreePosts.balance : 0;
            const after = account.balance;
            const diff = after - before;
            if (diff > 0) {
              enqueueSnackbar(
                <Trans>You have received {diff} free posts</Trans>,
                { variant: "success" },
              );
            }
          }
          queryClient.invalidateQueries(useEpBillingAccount.KEY, {
            refetchInactive: true,
          });
        },

        appBillingBillPaid: (_body: {
          data: Omit<
            IApiEpBillingBill,
            "invoice_url" | "payment_url" | "recurring_payment" | "receipt_url"
          >;
        }) => {
          dataLayerPushEcommerce(_body.data);

          queryClient.invalidateQueries("infinite__ep.billing.bill", {
            refetchInactive: true,
          });
        },

        appBillingRecurringPaymentUpdated: () => {
          queryClient.invalidateQueries(useEpBillingRecurringPayment.KEY, {
            refetchInactive: true,
          });
          queryClient.invalidateQueries(
            useEpBillingCurrentPlansubscription.KEY,
            {
              refetchInactive: true,
            },
          );
        },

        appEasypostActiveFeatureUpdated: (_body: {
          data: {
            id: IApiEpBillingActivefeature["id"];
            feature: IApiEpBillingActivefeature["feature"];
            period_end: IApiEpBillingActivefeature["period_end"];
            period_start: IApiEpBillingActivefeature["period_start"];
          };
        }) => {
          queryClient.invalidateQueries("ep.billing.active_feature", {
            refetchInactive: true,
          });
          queryClient.invalidateQueries(useEpBillingAccount.KEY, {
            refetchInactive: true,
          }); // WARNING: we cant invalidate accounts on accounts change, no message @zok
          queryClient.invalidateQueries(useQueryWatermarkConfig.KEY);
        },

        appEasypostPlanSubscriptionUpdated: (_body: {
          data: {
            id: IApiEpBillingPlansubscription["id"];
            period_end: IApiEpBillingPlansubscription["period_end"];
            period_start: IApiEpBillingPlansubscription["period_start"];
            plan_id: IApiEpBillingPlansubscription["plan_id"];
          };
        }) => {
          queryClient.invalidateQueries(
            useEpBillingCurrentPlansubscription.KEY,
            {
              refetchInactive: true,
            },
          );
          queryClient.invalidateQueries(useEpBillingAccount.KEY, {
            refetchInactive: true,
          }); // WARNING: we cant invalidate accounts on accounts change, no message @zok
          queryClient.invalidateQueries(useQueryWatermarkConfig.KEY);
          queryClient.invalidateQueries(useEpBillingPlansubscriptionList.KEY, {
            refetchInactive: true,
          });
        },

        appEasypostPostUpdated: (_body: { data: IApiEpPost }) => {
          setTimeout(() => {
            queryClient.invalidateQueries(useEpBillingAccount.KEY);
            queryClient.invalidateQueries(
              usePostProjectQuery.getKey({ id: _body.data.project }),
            );
            queryClient.invalidateQueries([useEpPageconfigListRecursive.KEY]);
            queryClient.invalidateQueries(["infinite__ep.billing.split"]);
          }, 3000);
        },

        appUsersMeUpdated: (_body: {
          data: {
            billing_policy_id: IApiEpUserAuthorized["billing_policy_id"];
            billing_policy_base: IApiEpUserAuthorized["billing_policy_base"];
          };
        }) => {
          meInvalidate();
        },

        commonWebEvent: async (body: any) => {
          if (body.event === "notification") {
            const notification = body.data.notification;

            if (notification.class_id === "organization:member_role_updated") {
              return;
            }

            if (notification.title) {
              enqueueSnackbar(notification.title[locale], {
                variant:
                  notification.level === "danger"
                    ? "error"
                    : notification.level,
              });
              notificationIvalidate();
            } else {
              notificationIvalidate();
            }
          }
          if (body.event === "billing_status_update") {
            if (organizationList && organizationList.length > 0) {
              // most user dont have the teams
              // get with updated .is_active states
              queryClient.invalidateQueries(
                useEpOrganizationOrganizationList.KEY,
                {
                  refetchInactive: true,
                },
              );
            }
          }
          if (body.event === "membership_deleted_by_owner") {
            queryClient.invalidateQueries(
              useEpOrganizationOrganizationList.KEY,
            );
          }
        },

        pageSubscriptionsUpdated: ({
          data,
        }: {
          data: {
            credential: IApiSmpAccountCredential;
          };
        }) => {
          const KEY = [
            useProxyAccountContainer.KEY,
            { path: { id: data.credential.id } },
          ];

          queryClient.cancelQueries(KEY);

          queryClient.fetchQuery(KEY, () =>
            ApiProxy.AccountContainer.retrieve({
              path: { id: data.credential.id },
            }),
          );
        },

        streamDestinationUpdated: (body: any) => {
          const destination = body.data as IApiSmpStreamDestination;
          setSmpStreamDestinationsMap((smpStreamDestinationsMap) =>
            produce(smpStreamDestinationsMap, (draft) => {
              draft.set(destination.id, destination);
            }),
          );
          queryClient.setQueriesData(
            [useSmpStreamDestinationList.KEY],
            (destinationsCachedList: IApiSmpStreamDestination[] | undefined) =>
              (destinationsCachedList || []).map((destinationCached) =>
                destinationCached.id === destination.id
                  ? destination
                  : destinationCached,
              ),
          );
        },

        streamSourceUpdated: (body: any) => {
          const source = body.data as IApiSmpStreamSource;
          setSmpStreamSourcesMap((smpStreamSourcesMap) =>
            produce(smpStreamSourcesMap, (draft) => {
              draft.set(source.id, source);
            }),
          );
        },

        streamStreamUpdated: (body: any) => {
          const stream = body.data as IApiSmpStream;
          setSmpStreamsMap((smpStreamsMap) =>
            produce(smpStreamsMap, (draft) => {
              draft.set(stream.id, stream);
            }),
          );
          queryClient.setQueryData(
            useSmpStreamStreamList.KEY,
            (streamsCachedList: IApiSmpStream[] | undefined) =>
              (streamsCachedList || []).map((streamCached) =>
                streamCached.id === stream.id ? stream : streamCached,
              ),
          );
        },

        billingStatusUpdated: () => {
          queryClient.invalidateQueries("infinite__ep.billing.bill", {
            refetchInactive: true,
          });
        },

        artGalleryPolotnoCreated: (
          message: IWsMessage<{
            polotno: IApiSmpArtGalleryPolotnoUserTemplate;
          }>,
        ) => {
          const newTemplate = message.data.polotno;

          queryClient.setQueriesData<
            ReturnType<typeof useQueryPolotnoUserTemplates>["data"]
          >(
            {
              queryKey: [useQueryPolotnoUserTemplates.KEY, organizationId],
              predicate: (q) => {
                const queryKeyParams = q.queryKey.at(2) as
                  | Record<"params", Record<string, string>>
                  | undefined;

                return queryKeyParams?.params.folder_id === undefined;
              },
            },
            (data) => {
              if (
                data?.pages.some((page) =>
                  page.results.some((result) => result.id === newTemplate.id),
                )
              ) {
                return data;
              }

              return produce(data, (draft) => {
                if (draft?.pages) {
                  draft.pages[0].results = [
                    message.data.polotno,
                    ...draft.pages[0].results,
                  ];
                }
              });
            },
            {
              updatedAt: 0,
            },
          );
        },

        artGalleryPolotnoUpdated: (
          message: IWsMessage<{
            polotno: IApiSmpArtGalleryPolotnoUserTemplate;
          }>,
        ) => {
          queryClient.setQueriesData<
            ReturnType<typeof useQueryPolotnoUserTemplates>["data"]
          >(
            useQueryPolotnoUserTemplates.KEY,
            (data) =>
              produce(data, (draft) => {
                if (draft?.pages) {
                  draft?.pages.forEach((page) => {
                    page.results = page.results.map((result) =>
                      result.id === message.data.polotno.id
                        ? message.data.polotno
                        : result,
                    );
                  });
                }
              }),
            {
              updatedAt: 0,
            },
          );
        },

        artGalleryPolotnoDeleted: (
          message: IWsMessage<{
            polotno: { id: number };
          }>,
        ) => {
          const templateId = message.data.polotno.id;

          queryClient.setQueriesData<
            ReturnType<typeof useQueryPolotnoUserTemplates>["data"]
          >(useQueryPolotnoUserTemplates.KEY, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.results = page.results.filter(
                  (item) => item.id !== templateId,
                );
              });
            }),
          );

          queryClient.setQueriesData<
            ReturnType<typeof useQueryPolotnoFolders>["data"]
          >(useQueryPolotnoFolders.KEY, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.results.forEach((result) => {
                  result.polotno_ids = result.polotno_ids.filter(
                    (id) => id !== templateId,
                  );
                });
              });
            }),
          );
        },

        artGalleryUploadCreated: (
          message: IWsMessage<{ upload: IApiSmpArtGalleryUpload }>,
        ) => {
          const newUpload = message.data.upload;
          const mimetype = newUpload.mimetype;
          const type = mimetype.startsWith("image/")
            ? "image"
            : mimetype.startsWith("font/")
            ? "font"
            : null;

          if (!type) {
            return;
          }

          queryClient.setQueriesData<
            ReturnType<typeof useSmpArtGalleryUploadList>["data"]
          >(
            [
              useSmpArtGalleryUploadList.KEY,
              { params: { type }, organizationId },
            ],
            (data) => {
              if (
                data?.pages.some((page) =>
                  page.results.some((result) => result.id === newUpload.id),
                )
              ) {
                return data;
              }

              return produce(data, (draft) => {
                if (draft?.pages) {
                  draft.pages[0].results = [
                    newUpload,
                    ...draft.pages[0].results,
                  ];
                }
              });
            },
          );
        },

        artGalleryUploadDeleted: (
          message: IWsMessage<{ upload: { id: number } }>,
        ) => {
          const id = message.data.upload.id;

          queryClient.setQueriesData<
            ReturnType<typeof useSmpArtGalleryUploadList>["data"]
          >(useSmpArtGalleryUploadList.KEY, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.results = page.results.filter((item) => item.id !== id);
              });
            }),
          );

          queryClient.setQueriesData<
            ReturnType<typeof useQueryPolotnoBrandBooks>["data"]
          >(useQueryPolotnoBrandBooks.KEY, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.results.forEach((brandBook) => {
                  brandBook.elements = brandBook.elements.filter(
                    (element) => element.id !== id,
                  );
                });
              });
            }),
          );
        },

        repostСonfigСreated: () => {
          queryClient.invalidateQueries("infinite__smp.repost.repost_config");
        },

        repostСonfigUpdated: () => {
          queryClient.invalidateQueries("infinite__smp.repost.repost_config");
        },

        repostСonfigDeleted: () => {
          queryClient.invalidateQueries("infinite__smp.repost.repost_config");
        },

        gptPromptCreated: (
          message: IWsMessage<{ prompt: IApiSmpGptPrompt }>,
        ) => {
          queryClient.setQueriesData<
            ReturnType<typeof useQueryGptPromptList>["data"]
          >(useQueryGptPromptList.KEY, (data) => {
            if (
              data?.pages.some((page) =>
                page.results.some(
                  (prompt) => prompt.id === message.data.prompt.id,
                ),
              )
            ) {
              return data;
            }

            return produce(data, (draft) => {
              draft?.pages[0].results.unshift(message.data.prompt);
            });
          });
        },

        gptPromptDeleted: (message: IWsMessage<{ prompt: { id: number } }>) => {
          queryClient.setQueriesData<
            ReturnType<typeof useQueryGptPromptList>["data"]
          >(useQueryGptPromptList.KEY, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.results = page.results.filter(
                  (prompt) => prompt.id !== message.data.prompt.id,
                );
              });
            }),
          );
        },

        organizationMembershipDeletedByOwner: (
          message: IWsMessage<{
            membership_id: number | null;
            organization_id: number;
            user_id: number;
          }>,
        ) => {
          if (me.id !== message.data.user_id) {
            return;
          }

          const organizationSetKey =
            useQueryOrganizationSet.getKeys().organizationSet;

          queryClient.setQueriesData<
            UseInfiniteQueryResult<IOrganizationSetQuery>["data"]
          >(organizationSetKey, (data) =>
            produce(data, (draft) => {
              draft?.pages.forEach((page) => {
                page.organization_set = page.organization_set.filter(
                  (organization) =>
                    organization.id !== message.data.organization_id,
                );
              });
            }),
          );

          if (organizationId === message.data.organization_id) {
            organizationIdChange({ organizationId: null });
          }
        },

        organizationMembershipDeletedByMember: (
          message: IWsMessage<IApiEpOrganizationMembership>,
        ) => {
          queryClient.setQueriesData<
            ReturnType<typeof useQueryOrganizationMembershipList>["data"]
          >(
            [
              useQueryOrganizationMembershipList.KEY,
              { organization: message.data.organization },
            ],
            (data) =>
              produce(data, (draft) =>
                draft?.filter(
                  (membership) => membership.user.id !== message.data.user.id,
                ),
              ),
          );
        },

        organizationMembershipUpdated: (
          message: IWsMessage<iWsOrganizationMembershipUpdatedData>,
        ) => {
          queryClient.setQueriesData<
            ReturnType<typeof useQueryOrganizationMembershipList>["data"]
          >(
            [
              useQueryOrganizationMembershipList.KEY,
              { organization: message.data.organization },
            ],
            (data) =>
              produce(data, (draft) =>
                draft?.forEach((membership) => {
                  if (membership.id === message.data.id) {
                    membership.role = message.data.role;
                    membership.is_confirmed = message.data.is_confirmed;
                  }
                }),
              ),
          );

          queryClient.setQueriesData<
            UseInfiniteQueryResult<IOrganizationSetQuery>["data"] | undefined
          >(useEpOrganizationOrganizationList.KEY, (data) =>
            produce(data, (draft) =>
              draft?.pages.forEach((page) => {
                page.organization_set?.forEach((org) => {
                  if (
                    org.id === message.data.organization &&
                    me.id === message.data.user.id
                  ) {
                    org.role = message.data.role;
                  }
                });
              }),
            ),
          );
        },

        organizationPropagated: async (
          message: IWsMessage<IWsOrganizationPropagatedData>,
        ) => {
          socialPageGroupSetInvalidate();
          pageConfigSetInvalidate();

          queryClient.setQueriesData<
            UseInfiniteQueryResult<ISocialPageGroupSetQuery>["data"] | undefined
          >(queryGetMainKey(useInfiniteSocialPageGroupSetQuery), (data) =>
            produce(data, (draft) =>
              draft?.pages.forEach((page) => {
                page.social_page_group_set = page.social_page_group_set.map(
                  (group) => {
                    if (group.id === message.data.socialpagegroup_id) {
                      group.is_locked_for_update = false;
                      group.organization_after_update =
                        message.data.organization_after;
                      group.organization = message.data.organization_after;
                    }

                    return group;
                  },
                );
              }),
            ),
          );

          if (message.data.socialpagegroup_destroyed) {
            // SocialPageGroup destroyed
            enqueueSnackbar(
              <UiTypography>
                <Trans>
                  Social Set{" "}
                  <strong>{message.data.socialpagegroup_name}</strong> has been
                  deleted
                </Trans>
              </UiTypography>,
              { variant: "info" },
            );
          } else if (
            message.data.organization_id_after &&
            message.data.organization_id_before &&
            message.data.organization_id_after !==
              message.data.organization_id_before
          ) {
            enqueueSnackbar(
              <UiTypography>
                <Trans>
                  Social Set{" "}
                  <strong>{message.data.socialpagegroup_name}</strong> has been
                  transferred
                </Trans>
              </UiTypography>,
              { variant: "info" },
            );
          } else if (
            !message.data.organization_id_after &&
            message.data.organization_id_before
          ) {
            // SocialPageGroup revoked from Organization
            enqueueSnackbar(
              <UiTypography>
                <Trans>
                  Social Set{" "}
                  <strong>{message.data.socialpagegroup_name}</strong> has been
                  removed from the team
                </Trans>
              </UiTypography>,
              { variant: "info" },
            );
          } else if (
            message.data.organization_id_after &&
            !message.data.organization_id_before
          ) {
            enqueueSnackbar(
              <UiTypography>
                <Trans>
                  Social Set{" "}
                  <strong>{message.data.socialpagegroup_name}</strong> has been
                  moved to the team
                </Trans>
              </UiTypography>,
              { variant: "info" },
            );
          }
        },

        pageConfigCreated: () => {
          pageConfigSetInvalidate();
          socialPageGroupSetInvalidate();
        },

        pageConfigUpdated: () => {
          pageConfigSetInvalidate();
          socialPageGroupSetInvalidate();
        },

        pageConfigDeleted: (message: IWsMessage<iWsPageConfigData>) => {
          socialPageGroupSetUpdate((draft) => {
            draft?.social_page_group_set.forEach((group) => {
              group.pageconfig_set = group.pageconfig_set?.filter(
                ({ id }) => id !== message.data.id,
              );
            });
          });
        },

        socialPageGroupCreated: async (message: IWsMessage<{ id: number }>) => {
          const socialPageGroupCreated = await fetchSocialPageGroup(
            message.data.id,
          );

          if (socialPageGroupCreated) {
            const socialPageGroupKey =
              useQueryOrganizationSet.getKeys().socialPageGroupSet;

            queryClient.setQueriesData<
              | UseInfiniteQueryResult<ISocialPageGroupSetQuery>["data"]
              | undefined
            >(socialPageGroupKey, (data) =>
              produce(data, (draft) =>
                draft?.pages.forEach((page) => {
                  page.social_page_group_set.push(socialPageGroupCreated);
                }),
              ),
            );
          }
        },

        socialPageGroupUpdated: async (message: IWsMessage<{ id: number }>) => {
          const socialPageGroupUpdated = await fetchSocialPageGroup(
            message.data.id,
          );

          if (socialPageGroupUpdated) {
            socialPageGroupSetUpdate((draft) => {
              if (draft?.social_page_group_set) {
                draft.social_page_group_set = draft.social_page_group_set.map(
                  (group) =>
                    group.id === socialPageGroupUpdated.id
                      ? socialPageGroupUpdated
                      : group,
                );
              }
            });

            updatePageConfigSetAll((draft) => {
              draft?.pages.forEach((page) => {
                page.page_config_set.edges.forEach(({ node }) => {
                  if (node.group?.id === socialPageGroupUpdated?.id) {
                    node.group = socialPageGroupUpdated;
                  }
                });
              });
            });
          }
        },

        watermarkCreated: (message: IWsMessage<IWatermark>) => {
          queryClient.setQueriesData<IWatermarkSetQuery | undefined>(
            useWatermarkSetQuery.getKey(),
            (data) =>
              produce(data, (draft) => {
                draft = draft || {};
                draft.watermark_set = draft.watermark_set || [];

                if (
                  !draft.watermark_set.some(({ id }) => id === message.data.id)
                ) {
                  draft.watermark_set.push(message.data);
                }
              }),
            {
              updatedAt: 0,
            },
          );
        },

        watermarkUpdated: (message: IWsMessage<IWatermark>) => {
          queryClient.setQueriesData<IWatermarkSetQuery | undefined>(
            useWatermarkSetQuery.getKey(),
            (data) =>
              produce(data, (draft) => {
                if (draft?.watermark_set) {
                  draft.watermark_set = draft.watermark_set.map((watermark) =>
                    watermark.id === message.data.id ? message.data : watermark,
                  );
                }
              }),
            {
              updatedAt: 0,
            },
          );
        },

        watermarkDeleted: (message: IWsMessage<IWatermark>) => {
          queryClient.setQueriesData<IWatermarkSetQuery | undefined>(
            useWatermarkSetQuery.getKey(),
            (data) =>
              produce(data, (draft) => {
                if (draft?.watermark_set) {
                  draft.watermark_set = draft.watermark_set.filter(
                    (watermark) => watermark.id !== message.data.id,
                  );
                }
              }),
          );
        },
      }),
      [
        queryClient,
        accountFreePosts,
        enqueueSnackbar,
        meInvalidate,
        locale,
        notificationIvalidate,
        organizationList,
        setSmpStreamDestinationsMap,
        setSmpStreamSourcesMap,
        setSmpStreamsMap,
        organizationId,
        me.id,
        organizationIdChange,
        socialPageGroupSetUpdate,
        pageConfigSetInvalidate,
        socialPageGroupSetInvalidate,
        fetchSocialPageGroup,
        updatePageConfigSetAll,
      ],
    );

  return taskHandlers;
};
